import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Activity, AlertCircle, CheckCircle2, DatabaseZap, Loader2, RefreshCw } from 'lucide-react';

import {
  api,
  ApiRequestError,
  type ServiceDefinitionFormValue,
  type TrafficEvidenceRun,
  type TrafficInvestigationCase,
  type TrafficInvestigationDraft,
  type TrafficInvestigationPartyComparison,
  type TrafficInvestigationPreflight,
  type TrafficInvestigationSubscriptionComparison,
  type TrafficMetricDelta,
} from '@/services/api';
import { TrafficEvidenceRunHistory } from '@/components/TrafficEvidenceRunHistory';
import {
  canRetryEvidenceRun,
  evidenceDraftId,
  preflightEstimateLabel,
  trunkCoverageNotice,
} from '@/components/trafficEvidenceRunPresentation';
import {
  formRunCorrelationId,
  isRecoverableRunStartFailure,
  recoverTrafficEvidenceRun,
} from '@/components/trafficEvidenceRunRecovery';
import {
  sortSubscriptionComparisons,
  subscriptionCountComparison,
  subscriptionFailureComparison,
  subscriptionVolumeComparison,
  type SubscriptionCountComparison,
  type SubscriptionFailureComparison,
} from '@/components/trafficSubscriptionPresentation';
import { useChatStore } from '@/stores/chatStore';

function shiftedCalendarDate(dayOffset: number): string {
  const now = new Date();
  now.setDate(now.getDate() + dayOffset);
  return [
    String(now.getFullYear()).padStart(4, '0'),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'The Traffic Investigation could not be loaded.';
}

function number(value: number): string {
  return value.toLocaleString();
}

function change(metric: TrafficMetricDelta): string {
  const sign = metric.absoluteChange > 0 ? '+' : '';
  const percent = metric.percentChange === null
    ? ''
    : ` · ${metric.percentChange > 0 ? '+' : ''}${metric.percentChange.toFixed(1)}%`;
  return `${sign}${number(metric.absoluteChange)}${percent}`;
}

function MetricRow({ label, metric }: { label: string; metric: TrafficMetricDelta }) {
  return (
    <tr>
      <th className="px-3 py-2 text-left font-medium text-surface-700 dark:text-surface-200">{label}</th>
      <td className="px-3 py-2 text-right font-mono tabular-nums">{number(metric.comparison)}</td>
      <td className="px-3 py-2 text-right font-mono tabular-nums">{number(metric.current)}</td>
      <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums">{change(metric)}</td>
    </tr>
  );
}

function PartyComparison({
  title,
  volumeLabel,
  comparison,
}: {
  title: string;
  volumeLabel: 'Incoming count' | 'Outgoing count';
  comparison: TrafficInvestigationPartyComparison;
}) {
  const volume = volumeLabel === 'Incoming count' ? comparison.incomingCount : comparison.outgoingCount;
  const coverageNotice = trunkCoverageNotice(comparison);
  return (
    <section className="overflow-hidden rounded-lg border border-surface-200 dark:border-surface-800">
      <h4 className="bg-surface-100 px-3 py-2.5 text-xs font-semibold text-surface-900 dark:bg-surface-900 dark:text-white">{title}</h4>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[32rem] text-xs">
          <thead className="bg-white text-[10px] uppercase tracking-wide text-surface-500 dark:bg-surface-950 dark:text-surface-400">
            <tr>
              <th className="px-3 py-2 text-left">Metric</th>
              <th className="px-3 py-2 text-right">Comparison</th>
              <th className="px-3 py-2 text-right">Current</th>
              <th className="px-3 py-2 text-right">Change</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-200 bg-white dark:divide-surface-800 dark:bg-surface-950">
            <MetricRow label="Completed count" metric={comparison.completedCount} />
            <MetricRow label={volumeLabel} metric={volume} />
            <MetricRow label="Failover count" metric={comparison.failoverCount} />
            <MetricRow label="Mapped trunks" metric={comparison.mappedTrunkCount} />
            <MetricRow label="Observed trunks" metric={comparison.observedTrunkCount} />
          </tbody>
        </table>
      </div>
      {coverageNotice && (
        <p role="note" className="border-t border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-5 text-amber-900 dark:border-amber-900 dark:bg-amber-950/25 dark:text-amber-200">
          {coverageNotice}
        </p>
      )}
    </section>
  );
}

function subscriptionCount(value: number | null): string {
  return value === null ? 'Unavailable' : number(value);
}

function subscriptionChange(value: number | null): string {
  if (value === null) return 'Unavailable';
  return `${value > 0 ? '+' : ''}${number(value)}`;
}

function CountComparisonCell({ values }: { values: SubscriptionCountComparison }) {
  return (
    <dl className="grid min-w-[9rem] grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-[11px]">
      <dt className="text-surface-500 dark:text-surface-400">Comparison</dt>
      <dd className="text-right font-mono tabular-nums">{subscriptionCount(values.comparison)}</dd>
      <dt className="text-surface-500 dark:text-surface-400">Current</dt>
      <dd className="text-right font-mono tabular-nums">{subscriptionCount(values.current)}</dd>
      <dt className="font-medium text-surface-600 dark:text-surface-300">Change</dt>
      <dd className="text-right font-mono font-semibold tabular-nums">{subscriptionChange(values.absoluteChange)}</dd>
    </dl>
  );
}

function FailureComparisonCell({ values }: { values: SubscriptionFailureComparison }) {
  return (
    <dl className="grid min-w-[10rem] grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-[11px]">
      <dt className="text-surface-500 dark:text-surface-400">Comparison</dt>
      <dd className="text-right font-mono tabular-nums">{values.comparison}</dd>
      <dt className="text-surface-500 dark:text-surface-400">Current</dt>
      <dd className="text-right font-mono tabular-nums">{values.current}</dd>
      <dt className="font-medium text-surface-600 dark:text-surface-300">Change</dt>
      <dd className="text-right font-semibold">{values.change}</dd>
    </dl>
  );
}

function SubscriptionComparisonTable({
  title,
  party,
  volumeLabel,
  subscriptions,
}: {
  title: string;
  party: 'customers' | 'providers';
  volumeLabel: 'Incoming volume' | 'Outgoing volume';
  subscriptions: TrafficInvestigationSubscriptionComparison[];
}) {
  const sortedSubscriptions = sortSubscriptionComparisons(subscriptions);
  return (
    <section className="mt-3 overflow-hidden rounded-lg border border-surface-200 dark:border-surface-800">
      <div className="bg-surface-100 px-3 py-2.5 dark:bg-surface-900">
        <h4 className="text-xs font-semibold text-surface-900 dark:text-white">{title}</h4>
        <p className="mt-0.5 text-[10px] text-surface-500 dark:text-surface-400">Platform subscriptions are sorted by the largest absolute completed-count change. Missing period evidence is unavailable.</p>
      </div>
      {sortedSubscriptions.length === 0 ? (
        <p className="bg-white px-3 py-4 text-xs text-surface-500 dark:bg-surface-950 dark:text-surface-400">No subscription comparison is available for this case.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[66rem] text-xs">
            <thead className="bg-white text-[10px] uppercase tracking-wide text-surface-500 dark:bg-surface-950 dark:text-surface-400">
              <tr>
                <th className="px-3 py-2 text-left">Platform subscription</th>
                <th className="px-3 py-2 text-left">Completed traffic</th>
                <th className="px-3 py-2 text-left">{volumeLabel}</th>
                <th className="px-3 py-2 text-left">Failover</th>
                <th className="px-3 py-2 text-left">Top SIP failure</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-200 bg-white dark:divide-surface-800 dark:bg-surface-950">
              {sortedSubscriptions.map((subscription) => (
                <tr key={`${subscription.carrierId}:${subscription.providerRecordId}`} className="align-top">
                  <th className="px-3 py-3 text-left font-normal">
                    <p className="font-semibold text-surface-900 dark:text-white">{subscription.providerRecordName}</p>
                    <p className="mt-1 text-[10px] leading-4 text-surface-500 dark:text-surface-400">{subscription.carrierName} · Platform ID {subscription.providerRecordId}</p>
                  </th>
                  <td className="px-3 py-3"><CountComparisonCell values={subscriptionCountComparison(subscription, 'completedCount')} /></td>
                  <td className="px-3 py-3"><CountComparisonCell values={subscriptionVolumeComparison(subscription, party)} /></td>
                  <td className="px-3 py-3"><CountComparisonCell values={subscriptionCountComparison(subscription, 'failoverCount')} /></td>
                  <td className="px-3 py-3"><FailureComparisonCell values={subscriptionFailureComparison(subscription)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SipDistribution({ casebook }: { casebook: TrafficInvestigationCase }) {
  return (
    <section className="mt-3 overflow-hidden rounded-lg border border-surface-200 dark:border-surface-800">
      <div className="bg-surface-100 px-3 py-2.5 dark:bg-surface-900">
        <h4 className="text-xs font-semibold text-surface-900 dark:text-white">Full SIP response distribution</h4>
        <p className="mt-0.5 text-[10px] text-surface-500 dark:text-surface-400">All bounded response groups are shown, including zero counts.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[50rem] text-xs">
          <thead className="bg-white text-[10px] uppercase tracking-wide text-surface-500 dark:bg-surface-950 dark:text-surface-400">
            <tr>
              <th className="px-3 py-2 text-left">SIP response</th>
              <th className="px-3 py-2 text-right">Customer comparison</th>
              <th className="px-3 py-2 text-right">Customer current</th>
              <th className="px-3 py-2 text-right">Customer change</th>
              <th className="px-3 py-2 text-right">Provider comparison</th>
              <th className="px-3 py-2 text-right">Provider current</th>
              <th className="px-3 py-2 text-right">Provider change</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-200 bg-white font-mono tabular-nums dark:divide-surface-800 dark:bg-surface-950">
            {casebook.comparisons.customers.sipResponses.map((customer, index) => {
              const provider = casebook.comparisons.providers.sipResponses[index];
              return (
                <tr key={customer.code}>
                  <th className="px-3 py-2 text-left font-semibold">{customer.code}</th>
                  <td className="px-3 py-2 text-right">{number(customer.counts.comparison)}</td>
                  <td className="px-3 py-2 text-right">{number(customer.counts.current)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{change(customer.counts)}</td>
                  <td className="px-3 py-2 text-right">{number(provider?.counts.comparison ?? 0)}</td>
                  <td className="px-3 py-2 text-right">{number(provider?.counts.current ?? 0)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{provider ? change(provider.counts) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EvidenceList({ title, values, empty }: {
  title: string;
  values: Array<{ statement: string }>;
  empty: string;
}) {
  return (
    <section>
      <h4 className="text-xs font-semibold text-surface-900 dark:text-white">{title}</h4>
      {values.length === 0 ? (
        <p className="mt-1.5 text-xs leading-5 text-surface-500 dark:text-surface-400">{empty}</p>
      ) : (
        <ul className="mt-1.5 space-y-1.5 text-xs leading-5 text-surface-600 dark:text-surface-300">
          {values.map((value, index) => <li key={`${title}:${index}`}>• {value.statement}</li>)}
        </ul>
      )}
    </section>
  );
}

export function DomesticTrafficPerformance({
  disabled = false,
  draftId,
}: {
  disabled?: boolean;
  draftId?: string;
}) {
  const activeSessionId = useChatStore((state) => state.activeSessionId);
  const [services, setServices] = useState<ServiceDefinitionFormValue[]>([]);
  const [serviceDefinitionId, setServiceDefinitionId] = useState('');
  const [question, setQuestion] = useState('Why did traffic change between these periods?');
  const [currentStart, setCurrentStart] = useState(() => shiftedCalendarDate(-6));
  const [currentEnd, setCurrentEnd] = useState(() => shiftedCalendarDate(0));
  const [comparisonStart, setComparisonStart] = useState(() => shiftedCalendarDate(-13));
  const [comparisonEnd, setComparisonEnd] = useState(() => shiftedCalendarDate(-7));
  const [loading, setLoading] = useState(true);
  const [investigating, setInvestigating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runNotice, setRunNotice] = useState<string | null>(null);
  const mounted = useRef(true);
  const [clarification, setClarification] = useState<string[]>([]);
  const [draft, setDraft] = useState<TrafficInvestigationDraft | null>(null);
  const [preflight, setPreflight] = useState<TrafficInvestigationPreflight | null>(null);
  const [scopeReviewed, setScopeReviewed] = useState(false);
  const [runs, setRuns] = useState<TrafficEvidenceRun[]>([]);
  const [casebook, setCasebook] = useState<TrafficInvestigationCase | null>(null);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [feedbackSaved, setFeedbackSaved] = useState(false);

  // `targetDraftId` lets a recovered run reload its own draft. Without it, a
  // form-started screen would load the latest tenant case instead.
  const load = useCallback(async (targetDraftId: string | undefined = draftId) => {
    setLoading(true);
    setError(null);
    setScopeReviewed(false);
    try {
      const serviceState = await api.carrierInitialization.getServiceDefinitions();
      const definitions = serviceState?.definitions ?? [];
      setServices(definitions);
      const latest = targetDraftId ? null : await api.traffic.getLatestInvestigation();
      const resolvedDraftId = evidenceDraftId(targetDraftId, latest?.attribution);
      if (resolvedDraftId) {
        const [draftResponse, runHistory] = await Promise.all([
          api.traffic.getInvestigationDraft(resolvedDraftId),
          api.traffic.getInvestigationRuns(resolvedDraftId),
        ]);
        const savedDraft = draftResponse.draft;
        const savedCaseId = savedDraft.caseId
          ?? [...runHistory].reverse().find((run) => run.caseId)?.caseId
          ?? null;
        const savedCase = latest?.id === savedCaseId
          ? latest
          : savedCaseId
          ? await api.traffic.getInvestigation(savedCaseId)
          : null;
        setDraft(savedDraft);
        setPreflight(targetDraftId ? draftResponse.preflight : null);
        setRuns(runHistory);
        setCasebook(savedCase);
        setQuestion(savedDraft.originalQuestion);
        setServiceDefinitionId(savedDraft.serviceDefinitionId ?? definitions[0]?.id ?? '');
        if (savedDraft.currentPeriod) {
          setCurrentStart(savedDraft.currentPeriod.startDate);
          setCurrentEnd(savedDraft.currentPeriod.endDate);
        }
        if (savedDraft.comparisonPeriod) {
          setComparisonStart(savedDraft.comparisonPeriod.startDate);
          setComparisonEnd(savedDraft.comparisonPeriod.endDate);
        }
      } else {
        setServiceDefinitionId((current) => current || definitions[0]?.id || '');
        setCasebook(latest);
        setDraft(null);
        setPreflight(null);
        setRuns([]);
      }
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [draftId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const caseService = useMemo(
    () => services.find((service) => service.id === casebook?.serviceDefinitionId) ?? null,
    [casebook?.serviceDefinitionId, services],
  );

  const invalidatePreflight = () => {
    setPreflight(null);
    setScopeReviewed(false);
    setDraft(null);
    setRuns([]);
  };

  const reviewScope = async () => {
    if (!serviceDefinitionId || !activeSessionId || disabled) return;
    setInvestigating(true);
    setError(null);
    setClarification([]);
    setFeedbackSaved(false);
    try {
      const response = await api.traffic.preflightInvestigation({
        chatSessionId: activeSessionId,
        question,
        serviceDefinitionId,
        currentPeriod: { startDate: currentStart, endDate: currentEnd },
        comparisonPeriod: { startDate: comparisonStart, endDate: comparisonEnd },
      });
      const runHistory = await api.traffic.getInvestigationRuns(response.draft.id);
      const savedCase = response.draft.caseId
        ? await api.traffic.getInvestigation(response.draft.caseId)
        : null;
      setDraft(response.draft);
      setPreflight(response.preflight);
      setRuns(runHistory);
      setCasebook(savedCase);
      setScopeReviewed(true);
    } catch (investigationError) {
      if (investigationError instanceof ApiRequestError && investigationError.preflight) {
        setPreflight(investigationError.preflight);
      }
      if (investigationError instanceof ApiRequestError
        && investigationError.errorCode === 'traffic_investigation_clarification_required') {
        setClarification(investigationError.clarificationQuestions);
      } else {
        setError(errorMessage(investigationError));
      }
    } finally {
      setInvestigating(false);
    }
  };

  const startInvestigation = async () => {
    if (!draft || draft.state !== 'ready' || !preflight?.allowed || !activeSessionId || disabled) return;
    setInvestigating(true);
    setError(null);
    setClarification([]);
    setRunNotice(null);
    const knownRunIds = new Set(runs.map((run) => run.id));
    try {
      const response = await api.traffic.startInvestigationRun({
        draftId: draft.id,
        chatSessionId: activeSessionId,
        expectedDraftRevision: draft.revision,
      });
      setDraft(response.draft);
      setRuns([response.run]);
      setCasebook(response.case);
    } catch (investigationError) {
      if (!isRecoverableRunStartFailure(investigationError)) {
        setError(errorMessage(investigationError));
        return;
      }
      setRunNotice('The start result is unknown. Checking the run history.');
      const recovery = await recoverTrafficEvidenceRun({
        listRuns: () => api.traffic.getInvestigationRuns(draft.id),
        knownRunIds,
        requestCorrelationId: formRunCorrelationId(draft.id, draft.revision),
        onRunFound: () => {
          if (mounted.current) {
            setRunNotice('The investigation is still running. This can take a few minutes.');
          }
        },
        sleep: (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)),
        now: Date.now,
        shouldContinue: () => mounted.current,
      });
      if (recovery.outcome === 'cancelled') return;
      if (recovery.outcome === 'finished') {
        setRunNotice(null);
        await load(recovery.run.draftId);
      } else if (recovery.outcome === 'still_running') {
        setRuns([recovery.run]);
        setRunNotice('The investigation is still running. Reload this page later to see the result.');
      } else if (recovery.outcome === 'history_unavailable') {
        setRunNotice(null);
        setError('TelcoOS could not check whether the investigation started. Reload this page later to see the result.');
      } else {
        setRunNotice(null);
        setError(errorMessage(investigationError));
      }
    } finally {
      setInvestigating(false);
    }
  };

  const confirm = async () => {
    if (!casebook || disabled) return;
    setUpdating(true);
    setError(null);
    try {
      setCasebook(await api.traffic.confirmInvestigation(casebook.id));
    } catch (updateError) {
      setError(errorMessage(updateError));
    } finally {
      setUpdating(false);
    }
  };

  const saveFeedback = async (helpful?: boolean) => {
    if (!casebook || disabled || (helpful === undefined && !feedbackNote.trim())) return;
    setUpdating(true);
    setError(null);
    try {
      setCasebook(await api.traffic.addInvestigationFeedback(casebook.id, {
        ...(helpful === undefined ? {} : { helpful }),
        ...(feedbackNote.trim() ? { note: feedbackNote.trim() } : {}),
      }));
      setFeedbackNote('');
      setFeedbackSaved(true);
    } catch (updateError) {
      setError(errorMessage(updateError));
    } finally {
      setUpdating(false);
    }
  };

  const dateInputs: Array<[string, string, (value: string) => void]> = [
    ['Comparison start', comparisonStart, setComparisonStart],
    ['Comparison end', comparisonEnd, setComparisonEnd],
    ['Current start', currentStart, setCurrentStart],
    ['Current end', currentEnd, setCurrentEnd],
  ];

  return (
    <section aria-label="Traffic Investigation" className="mt-3 w-[min(76rem,calc(100vw-7rem))] max-w-full overflow-hidden rounded-xl border border-surface-200 bg-surface-50 shadow-sm dark:border-surface-700 dark:bg-surface-950">
      <header className="border-b border-surface-200 bg-white px-4 py-3.5 dark:border-surface-800 dark:bg-surface-900">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"><Activity className="h-4 w-4" /></span>
          <div>
            <p className="text-[0.65rem] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">Traffic Intelligence</p>
            <h3 className="mt-1 text-base font-semibold text-surface-950 dark:text-white">{draftId ? 'Traffic Investigation' : 'Ask why traffic changed'}</h3>
            <p className="mt-1 text-xs leading-5 text-surface-500 dark:text-surface-400">{draftId ? 'Review the saved evidence source, scope, completeness, and request history.' : 'Start one evidence-backed case. Compare two equal periods for one Service.'}</p>
          </div>
        </div>
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300"><DatabaseZap className="h-3 w-3" /> Aggregate reads · No CDR access</div>
      </header>

      {!draftId && (!scopeReviewed || runs.length === 0) && <div className="space-y-3 bg-white p-4 dark:bg-surface-900">
        <label className="block text-xs font-semibold text-surface-700 dark:text-surface-200">
          Investigation question
          <textarea value={question} onChange={(event) => { invalidatePreflight(); setQuestion(event.target.value); }} disabled={disabled || loading} rows={2} maxLength={500} className="mt-1.5 w-full resize-y rounded-lg border border-surface-300 bg-white px-3 py-2 text-sm text-surface-950 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 disabled:opacity-50 dark:border-surface-700 dark:bg-surface-950 dark:text-white" />
        </label>
        <div className="grid gap-3 md:grid-cols-[minmax(14rem,1fr)_repeat(4,10rem)]">
          <label className="block text-xs font-semibold text-surface-700 dark:text-surface-200">
            Service
            <select value={serviceDefinitionId} onChange={(event) => { invalidatePreflight(); setServiceDefinitionId(event.target.value); }} disabled={disabled || loading || services.length === 0} className="mt-1.5 h-10 w-full rounded-lg border border-surface-300 bg-white px-3 text-sm text-surface-950 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 disabled:opacity-50 dark:border-surface-700 dark:bg-surface-950 dark:text-white">
              {services.length === 0 && <option value="">No Services available</option>}
              {services.map((service) => <option key={service.id} value={service.id}>{service.code} — {service.name}</option>)}
            </select>
          </label>
          {dateInputs.map(([label, value, setter]) => (
            <label key={label} className="block text-xs font-semibold text-surface-700 dark:text-surface-200">
              {label}
              <input type="date" value={value} onChange={(event) => { invalidatePreflight(); setter(event.target.value); }} disabled={disabled || loading} className="mt-1.5 h-10 w-full rounded-lg border border-surface-300 bg-white px-2 text-xs text-surface-950 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 disabled:opacity-50 dark:border-surface-700 dark:bg-surface-950 dark:text-white" />
            </label>
          ))}
        </div>
        {!preflight && <button type="button" onClick={() => void reviewScope()} disabled={disabled || loading || investigating || !serviceDefinitionId || !activeSessionId} className="flex h-10 items-center justify-center gap-2 rounded-lg bg-surface-900 px-4 text-xs font-semibold text-white hover:bg-surface-700 disabled:opacity-50 dark:bg-white dark:text-surface-950 dark:hover:bg-surface-200">
          {investigating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Review request estimate
        </button>}
        {preflight && <div className={`rounded-lg border p-3 ${preflight.allowed ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/25' : 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/25'}`}>
          <p className={`text-xs font-semibold ${preflight.allowed ? 'text-emerald-900 dark:text-emerald-200' : 'text-red-800 dark:text-red-200'}`}>Request estimate</p>
          <p className={`mt-1 text-[11px] leading-4 ${preflight.allowed ? 'text-emerald-800 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>{preflightEstimateLabel(preflight)}</p>
          <p className="mt-1 text-[10px] text-surface-500 dark:text-surface-400">Maximum for one investigation: {preflight.maximumRequestCount} requests. Batches contain at most {preflight.providerRecordIdsPerRequest} record IDs.</p>
          {preflight.rejection && <p className="mt-2 text-[11px] text-red-700 dark:text-red-300">{preflight.rejection.message}</p>}
        </div>}
        {preflight?.allowed && draft && runs.length === 0 && <button type="button" onClick={() => void startInvestigation()} disabled={disabled || investigating || !activeSessionId} className="flex h-10 items-center justify-center gap-2 rounded-lg bg-surface-900 px-4 text-xs font-semibold text-white hover:bg-surface-700 disabled:opacity-50 dark:bg-white dark:text-surface-950 dark:hover:bg-surface-200">
          {investigating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Start investigation
        </button>}
      </div>}

      {draftId && draft?.state === 'cancelled' && runs.length === 0 && <div className="border-t border-surface-200 px-4 py-3 text-xs text-surface-600 dark:border-surface-800 dark:text-surface-300">A newer traffic question in this chat replaced this one. No traffic data was read for it.</div>}
      {draftId && draft?.state === 'ready' && preflight && runs.length === 0 && <div className="space-y-3 bg-white p-4 dark:bg-surface-900">
        <p className="text-xs font-semibold text-surface-900 dark:text-white">{draft.originalQuestion}</p>
        <dl className="grid gap-2 text-[11px] text-surface-700 dark:text-surface-200 md:grid-cols-3">
          <div><dt className="font-semibold">Service</dt><dd>{services.find((service) => service.id === draft.serviceDefinitionId)?.code ?? '—'}</dd></div>
          <div><dt className="font-semibold">Current period</dt><dd>{draft.currentPeriod ? `${draft.currentPeriod.startDate}–${draft.currentPeriod.endDate}` : '—'}</dd></div>
          <div><dt className="font-semibold">Comparison period</dt><dd>{draft.comparisonPeriod ? `${draft.comparisonPeriod.startDate}–${draft.comparisonPeriod.endDate}` : '—'}</dd></div>
        </dl>
        <div className={`rounded-lg border p-3 ${preflight.allowed ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/25' : 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/25'}`}>
          <p className={`text-xs font-semibold ${preflight.allowed ? 'text-emerald-900 dark:text-emerald-200' : 'text-red-800 dark:text-red-200'}`}>Request estimate</p>
          <p className={`mt-1 text-[11px] leading-4 ${preflight.allowed ? 'text-emerald-800 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>{preflightEstimateLabel(preflight)}</p>
          {preflight.rejection && <p className="mt-2 text-[11px] text-red-700 dark:text-red-300">{preflight.rejection.message}</p>}
        </div>
        <p className="text-[11px] text-surface-600 dark:text-surface-300">Check the Service and both periods. No traffic data is read until you start the investigation.</p>
        {preflight.allowed && <button type="button" onClick={() => void startInvestigation()} disabled={disabled || investigating || !activeSessionId} className="flex h-10 items-center justify-center gap-2 rounded-lg bg-surface-900 px-4 text-xs font-semibold text-white hover:bg-surface-700 disabled:opacity-50 dark:bg-white dark:text-surface-950 dark:hover:bg-surface-200">
          {investigating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Start investigation
        </button>}
      </div>}

      {clarification.length > 0 && <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/25 dark:text-amber-200"><p className="font-semibold">One clarification</p><ul className="mt-1.5 space-y-1">{clarification.map((item) => <li key={item}>• {item}</li>)}</ul></div>}
      {runNotice && <div role="status" className="flex items-start gap-2 border-t border-surface-200 px-4 py-3 text-xs text-surface-600 dark:border-surface-800 dark:text-surface-300">{investigating && <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin" />}<span>{runNotice}</span></div>}
      {error && <div role="alert" className="flex items-start gap-2 border-t border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300"><AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{error}</span>{!casebook && <button type="button" onClick={() => void load()} className="ml-auto flex shrink-0 items-center gap-1 font-semibold"><RefreshCw className="h-3 w-3" />Retry</button>}</div>}
      {!casebook && runs.length === 0 && !error && clarification.length === 0 && <div className="border-t border-surface-200 px-4 py-7 text-center text-xs text-surface-500 dark:border-surface-800 dark:text-surface-400">{loading ? 'Loading the tenant casebook…' : preflight ? 'Review the estimate, then start the investigation.' : 'Ask one question to create the first investigation case.'}</div>}

      {!casebook && runs.length > 0 && (
        <div className="border-t border-surface-200 bg-surface-50 p-4 dark:border-surface-800 dark:bg-surface-950">
          <h3 className="text-sm font-semibold text-surface-950 dark:text-white">{draft?.originalQuestion || 'Traffic Investigation evidence attempt'}</h3>
          <p className="mt-1 text-[11px] leading-4 text-surface-600 dark:text-surface-300">No confirmable Case Interpretation was created.{canRetryEvidenceRun(runs[runs.length - 1].status) && <> Send <span className="font-mono">Retry the investigation.</span> in this chat to retry the complete scope.</>}</p>
          <TrafficEvidenceRunHistory runs={runs} legacy={false} />
        </div>
      )}

      {casebook && (
        <div className="border-t border-surface-200 bg-surface-50 p-4 dark:border-surface-800 dark:bg-surface-950">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2"><span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${casebook.status === 'confirmed' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'}`}>{casebook.status}</span><span className="text-[10px] text-surface-500 dark:text-surface-400">Saved tenant case · {casebook.id.slice(-8)}</span></div>
              <h3 className="mt-2 text-sm font-semibold text-surface-950 dark:text-white">{casebook.question}</h3>
              <p className="mt-1 text-[11px] text-surface-500 dark:text-surface-400">{caseService?.code ?? casebook.serviceDefinitionId} · {casebook.comparisonPeriod.startDate}–{casebook.comparisonPeriod.endDate} compared with {casebook.currentPeriod.startDate}–{casebook.currentPeriod.endDate}</p>
            </div>
            {casebook.status === 'proposed' && <button type="button" onClick={() => void confirm()} disabled={disabled || updating} className="flex h-9 items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 text-xs font-semibold text-emerald-800 disabled:opacity-50 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300">{updating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}Confirm interpretation</button>}
          </div>
          <div className="mt-3 rounded-lg border border-surface-200 bg-white p-3 text-xs leading-5 text-surface-700 dark:border-surface-800 dark:bg-surface-900 dark:text-surface-200"><p className="font-semibold text-surface-950 dark:text-white">Evidence-backed explanation</p><p className="mt-1">{casebook.explanation}</p></div>
          <TrafficEvidenceRunHistory runs={runs} legacy={casebook.attribution?.status !== 'attributed'} />
          <div className="mt-3 grid gap-3 lg:grid-cols-2"><PartyComparison title="Customer aggregate comparison" volumeLabel="Incoming count" comparison={casebook.comparisons.customers} /><PartyComparison title="Provider aggregate comparison" volumeLabel="Outgoing count" comparison={casebook.comparisons.providers} /></div>
          <SubscriptionComparisonTable title="Customer subscription comparison" party="customers" volumeLabel="Incoming volume" subscriptions={casebook.subscriptions?.customers ?? []} />
          <SubscriptionComparisonTable title="Provider subscription comparison" party="providers" volumeLabel="Outgoing volume" subscriptions={casebook.subscriptions?.providers ?? []} />
          <SipDistribution casebook={casebook} />
          <div className="mt-3 grid gap-4 rounded-lg border border-surface-200 bg-white p-3 lg:grid-cols-3 dark:border-surface-800 dark:bg-surface-900"><EvidenceList title="Supporting evidence" values={casebook.supportingEvidence} empty="No supporting aggregate signal was identified." /><EvidenceList title="Contradicting evidence" values={casebook.contradictingEvidence} empty="No aggregate metric clearly contradicted the observed direction." /><section><h4 className="text-xs font-semibold text-surface-900 dark:text-white">Missing evidence</h4><ul className="mt-1.5 space-y-1.5 text-xs leading-5 text-surface-600 dark:text-surface-300">{casebook.missingEvidence.map((item) => <li key={item}>• {item}</li>)}</ul></section></div>
          <div className="mt-3 rounded-lg border border-surface-200 bg-white p-3 dark:border-surface-800 dark:bg-surface-900">
            <p className="text-xs font-semibold text-surface-900 dark:text-white">Optional feedback</p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row"><input value={feedbackNote} onChange={(event) => setFeedbackNote(event.target.value)} maxLength={1000} placeholder="Add a note about this case" disabled={disabled || updating} className="h-9 flex-1 rounded-lg border border-surface-300 bg-white px-3 text-xs outline-none focus:border-amber-500 dark:border-surface-700 dark:bg-surface-950" /><button type="button" onClick={() => void saveFeedback(true)} disabled={disabled || updating} className="h-9 rounded-lg border border-surface-300 px-3 text-xs font-semibold disabled:opacity-50 dark:border-surface-700">Helpful</button><button type="button" onClick={() => void saveFeedback(false)} disabled={disabled || updating} className="h-9 rounded-lg border border-surface-300 px-3 text-xs font-semibold disabled:opacity-50 dark:border-surface-700">Not helpful</button><button type="button" onClick={() => void saveFeedback()} disabled={disabled || updating || !feedbackNote.trim()} className="h-9 rounded-lg bg-surface-900 px-3 text-xs font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-surface-950">Save note</button></div>
            {feedbackSaved && <p className="mt-2 text-[11px] text-emerald-700 dark:text-emerald-300">Feedback saved. Feedback remains separate from Training Memory.</p>}
          </div>
          <footer className="mt-3 text-[11px] leading-5 text-surface-500 dark:text-surface-400">The evidence ledger stores normalized SipNav aggregate metrics. It stores no raw provider response and no CDR. The case does not read or change routing policy or routing execution. Case-specific evidence stays separate from Training Memory.</footer>
        </div>
      )}
    </section>
  );
}
